import type { EditorCompositionResponse, UserInstrumentDto } from "@eu.mdotm.api/sphere";
import { builtInSort } from "@mdotm/shadcn/legacy/utils";
import type { Map as ImmutableMap } from "immutable";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import type { ReviewInstrument } from "./const";
import { ReviewHeader } from "./ReviewHeader";
import { useReviewCompositionQueries } from "./reviewQueries";
import { reviewSortRank, toReviewInstrument } from "./reviewRows";
import { WeightReview, type WeightReviewProps } from "./WeightReview";

/** Loads the rows of a review in weights; until they arrive the header stands alone, its Save disabled. */
export function WeightReviewContainer({
	uuid,
	...view
}: Omit<WeightReviewProps, "rows"> & { uuid: string }): React.JSX.Element {
	const { composition, userInstruments } = useReviewCompositionQueries(uuid, view.entity);
	const queryRows = useMappedQueries(
		([compositionEditor, userInstrumentsMap]) => toWeightReviewRows(compositionEditor, userInstrumentsMap),
		[],
		[composition, userInstruments],
	);

	return (
		<>
			{queryRows.data == null && (
				<ReviewHeader {...view.header} save={view.canEditComposition ? { kind: "loading" } : undefined} />
			)}
			<ReactQueryWrapperBase query={queryRows}>
				{(rows) => <WeightReview {...view} rows={rows} />}
			</ReactQueryWrapperBase>
		</>
	);
}

function toWeightReviewRows(
	compositionEditor: EditorCompositionResponse,
	userInstrumentsMap: ImmutableMap<string, UserInstrumentDto>,
): ReviewInstrument[] {
	return (compositionEditor.composition ?? [])
		.map((instrument) => toReviewInstrument(instrument, userInstrumentsMap.get(instrument.ticker ?? "") ?? {}))
		.sort((a, b) => builtInSort(reviewSortRank(a), reviewSortRank(b), "desc"));
}
